/**
 * Human-readable durations for rate-limit copy and auth-mail TTL (shared client/server).
 *
 * Units are coarsest **exact** only: a value becomes days/hours/minutes solely when it divides
 * evenly; otherwise it is rendered as seconds. That keeps “30 Minuten” from becoming “0 hours”.
 */

export type DurationLocale = "de" | "en";

function unitLabel(
  count: number,
  locale: DurationLocale,
  de: { one: string; other: string },
  en: { one: string; other: string },
): string {
  if (locale === "de") return `${count} ${count === 1 ? de.one : de.other}`;
  return `${count} ${count === 1 ? en.one : en.other}`;
}

/** Coarsest exact unit: days, else hours, else minutes, else seconds. */
export function humanizeDuration(seconds: number, locale: DurationLocale): string {
  const n = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;

  if (n >= 86_400 && n % 86_400 === 0) {
    return unitLabel(
      n / 86_400,
      locale,
      { one: "Tag", other: "Tage" },
      { one: "day", other: "days" },
    );
  }
  if (n >= 3_600 && n % 3_600 === 0) {
    return unitLabel(
      n / 3_600,
      locale,
      { one: "Stunde", other: "Stunden" },
      { one: "hour", other: "hours" },
    );
  }
  if (n >= 60 && n % 60 === 0) {
    return unitLabel(
      n / 60,
      locale,
      { one: "Minute", other: "Minuten" },
      { one: "minute", other: "minutes" },
    );
  }
  return unitLabel(
    n,
    locale,
    { one: "Sekunde", other: "Sekunden" },
    { one: "second", other: "seconds" },
  );
}

/** Remaining wait as `m:ss` with zero-padded seconds (e.g. `1:00`, `0:09`). */
export function formatCountdown(seconds: number): string {
  const n = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const minutes = Math.floor(n / 60);
  const rest = n % 60;
  return `${minutes}:${rest.toString().padStart(2, "0")}`;
}
